import React from "react";
import { Bell } from "lucide-react";
import Chip from "./Chip";

/** An email exactly as it will be sent (the SwiftBoard layout), in a sandboxed frame that runs no scripts. */
function EmailPreview({ subject, html }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-text_secondary">
        <span className="text-text_muted">Subject:</span> <span className="font-medium text-text_primary">{subject || "—"}</span>
      </p>
      <iframe title="Email preview" sandbox="" srcDoc={html} className="h-[520px] w-full rounded-lg border border-border bg-surface" />
    </div>
  );
}

/** An in-app notification as the bell shows it. */
function InAppPreview({ rendered }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-surface p-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary_tint text-primary">
        <Bell size={15} />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-xs font-semibold text-text_primary">{rendered.title || "—"}</p>
          {rendered.varient && <Chip>{rendered.varient}</Chip>}
        </div>
        <p className="whitespace-pre-wrap break-words text-xs text-text_secondary">{rendered.message || "—"}</p>
        {rendered.action_url && <p className="truncate font-mono text-[11px] text-text_muted">{rendered.action_url}</p>}
      </div>
    </div>
  );
}

/** Any other channel: each rendered field as text, in `fields` order ([{ key, label }]) or as they come. */
function FieldsPreview({ rendered, fields }) {
  const list = fields ?? Object.keys(rendered).map((key) => ({ key, label: key }));
  return (
    <dl className="divide-y divide-border rounded-lg border border-border">
      {list.map(({ key, label }) => (
        <div key={key} className="px-3 py-2">
          <dt className="text-[11px] text-text_muted">{label}</dt>
          <dd className="whitespace-pre-wrap break-words text-xs text-text_primary">{Array.isArray(rendered[key]) ? rendered[key].join(", ") : String(rendered[key] ?? "—")}</dd>
        </div>
      ))}
    </dl>
  );
}

// One rendered reminder message (template preview, rule test run): an email in its layout, an in-app card, or the
// fields of any other channel.
export default function RenderedMessage({ channelKey, rendered, emailHtml, fields }) {
  if (!rendered) return null;
  if (channelKey === "EMAIL" && emailHtml) return <EmailPreview subject={rendered.subject} html={emailHtml} />;
  if (channelKey === "IN_APP") return <InAppPreview rendered={rendered} />;
  return <FieldsPreview rendered={rendered} fields={fields} />;
}
